import React from 'react';
import {
  TopicFilterKey,
  useTopicBuilderActions,
  useTopicBuilderFilters
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Store/useTopicBuilderStore';
import ChipItem from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/ChipItem';
import GenericSidebarFilter, {
  GenericSidebarFilterProps
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/GenericSidebarFilter';

import { SlSidebarScrollableContainer } from 'src/components/BeaconV2Components/SlSidebar';

interface TopicSidebarFilterProps extends GenericSidebarFilterProps {
  filterKey: TopicFilterKey;
  readOnly?: boolean;
}

const TopicSidebarFilter = (props: TopicSidebarFilterProps) => {
  const { setFilters } = useTopicBuilderActions();
  const { filters } = useTopicBuilderFilters();

  const currentInputs = filters[props.filterKey] as string[];
  const shouldRender = currentInputs?.length > 0 || !props.readOnly;
  /**
   * Placeholder state for the current input. Tracks the user input before the user presses enter to add a term to the list of currentInputs.
   */
  const [currentInput, setCurrentInput] = React.useState<string>('');

  /**
   * Renders the list of items as chips
   */
  const renderItems = React.useCallback(() => {
    return (
      <SlSidebarScrollableContainer marginTop="12px" marginBottom={currentInputs?.length ? '32px' : '16px'}>
        {currentInputs.map((input, index) => (
          <ChipItem
            key={index}
            label={input}
            handleDelete={
              props.readOnly
                ? undefined
                : () => {
                    setFilters(({ remove }) => remove({ key: props.filterKey, value: input }));
                  }
            }
          />
        ))}
      </SlSidebarScrollableContainer>
    );
  }, [props.filterKey, props.readOnly, currentInputs, setFilters]);

  /**
   * Handles the change of the input
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentInput(e.target.value);
  };

  /**
   * Handles the enter key press
   */
  const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setFilters(({ add }) => {
        add({ key: props.filterKey, values: [currentInput] });
      });

      setCurrentInput('');
    }
  };

  const handleClear = () => {
    setFilters(({ clear }) => clear({ key: props.filterKey }));
  };

  return (
    <>
      {shouldRender && (
        <GenericSidebarFilter
          {...props}
          includeClear={currentInputs.length > 0 && !props.readOnly}
          includeInput={!props.readOnly}
          renderItems={renderItems}
          handleClear={handleClear}
          sidebarInputProps={{
            ...props.sidebarInputProps,
            value: currentInput,
            onChange: handleChange,
            onKeyPress: handleEnter
          }}
        />
      )}
    </>
  );
};

export default React.memo(TopicSidebarFilter);
